import { ArrowLeft, Plus, X } from "lucide-react";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookContext } from "../contexts/BookContext";
import {
  useAcceptAnecdote,
  useCreateAnecdotes,
  useRejectAnecdote,
} from "../hooks/useAnecdotes";
import { CreateAnecdoteRequest } from "../types/api/anecdotes";
import { Anecdote } from "../types/models";
import {
  getDefaultAnecdoteMessage,
  getTimeUntilExpiration,
} from "../utils/anecdoteUtils";
import { AnecdoteCard } from "./AnecdoteCard";
import { AnecdoteFriendForm } from "./AnecdoteFriendForm";
import { AnecdoteStatusBadge } from "./AnecdoteStatusBadge";

type ModalView = "list" | "form" | "detail";

type AnecdotesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  chapterNumber: number;
  generatedChapterId?: number | null;
  chapterTitle: string;
};

export const AnecdotesModal = ({
  isOpen,
  onClose,
  chapterNumber,
  generatedChapterId,
  chapterTitle,
}: AnecdotesModalProps) => {
  const { t } = useTranslation();
  const { anecdotes, book, setAnecdotes } = useContext(BookContext);
  const [currentView, setCurrentView] = useState<ModalView>("list");
  const [selectedAnecdote, setSelectedAnecdote] = useState<Anecdote | null>(
    null
  );

  // Form state
  const [friends, setFriends] = useState<CreateAnecdoteRequest[]>([
    {
      friend_email: "",
      friend_first_name: "",
      friend_last_name: "",
      custom_message: "",
      chapter_number: chapterNumber,
      generated_chapter_id: generatedChapterId,
      book_id: book?.id || 0,
    },
  ]);

  const createAnecdotesMutation = useCreateAnecdotes();
  const acceptMutation = useAcceptAnecdote(selectedAnecdote?.id || 0);
  const rejectMutation = useRejectAnecdote(selectedAnecdote?.id || 0);

  // Filter anecdotes for current chapter
  const chapterAnecdotes = useMemo(() => {
    return anecdotes.filter((a) => a.chapter_number === chapterNumber);
  }, [anecdotes, chapterNumber]);

  // Count for each tab
  const sentCount = useMemo(
    () => chapterAnecdotes.filter((a) => a.status === "pending").length,
    [chapterAnecdotes]
  );
  const receivedCount = useMemo(
    () =>
      chapterAnecdotes.filter(
        (a) =>
          a.status === "submitted" ||
          a.status === "accepted" ||
          a.status === "rejected"
      ).length,
    [chapterAnecdotes]
  );
  const expiredCount = useMemo(
    () => chapterAnecdotes.filter((a) => a.status === "expired").length,
    [chapterAnecdotes]
  );

  // Reset state when modal closes
  const handleClose = () => {
    setCurrentView("list");
    setSelectedAnecdote(null);
    setFriends([
      {
        friend_email: "",
        friend_first_name: "",
        friend_last_name: "",
        custom_message: "",
        chapter_number: chapterNumber,
        generated_chapter_id: generatedChapterId,
        book_id: book?.id || 0,
      },
    ]);
    onClose();
  };

  // Navigate to detail view
  const handleViewDetail = (anecdote: Anecdote) => {
    setSelectedAnecdote(anecdote);
    setCurrentView("detail");
  };

  // Form handlers
  const handleAddFriend = () => {
    setFriends([
      ...friends,
      {
        friend_email: "",
        friend_first_name: "",
        friend_last_name: "",
        custom_message: "",
        chapter_number: chapterNumber,
        generated_chapter_id: generatedChapterId,
        book_id: book?.id || 0,
      },
    ]);
  };

  const handleRemoveFriend = (index: number) => {
    setFriends(friends.filter((_, i) => i !== index));
  };

  const handleFriendChange = (
    index: number,
    field: keyof CreateAnecdoteRequest,
    value: string
  ) => {
    const updated = [...friends];
    updated[index] = { ...updated[index], [field]: value };
    setFriends(updated);
  };

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all friends
    const validFriends = friends.filter(
      (f) => f.friend_email && f.friend_first_name && f.friend_last_name
    );

    if (validFriends.length === 0) {
      return;
    }

    // Add default message if custom_message is empty
    const friendsWithMessages = validFriends.map((f) => ({
      ...f,
      custom_message:
        f.custom_message ||
        getDefaultAnecdoteMessage(f.friend_first_name, chapterTitle, t),
    }));

    createAnecdotesMutation.mutate(
      { anecdotes: friendsWithMessages },
      {
        onSuccess: (newAnecdotes) => {
          setAnecdotes([...anecdotes, ...newAnecdotes]);
          setCurrentView("list");
          setFriends([
            {
              friend_email: "",
              friend_first_name: "",
              friend_last_name: "",
              custom_message: "",
              chapter_number: chapterNumber,
              generated_chapter_id: generatedChapterId,
              book_id: book?.id || 0,
            },
          ]);
        },
      }
    );
  };

  // Accept/Reject handlers
  const handleAccept = () => {
    if (!selectedAnecdote) return;

    acceptMutation.mutate(null, {
      onSuccess: (updatedAnecdote) => {
        setAnecdotes(
          anecdotes.map((a) =>
            a.id === updatedAnecdote.id ? updatedAnecdote : a
          )
        );
        setSelectedAnecdote(updatedAnecdote);
      },
    });
  };

  const handleReject = () => {
    if (!selectedAnecdote) return;

    rejectMutation.mutate(null, {
      onSuccess: (updatedAnecdote) => {
        setAnecdotes(
          anecdotes.map((a) =>
            a.id === updatedAnecdote.id ? updatedAnecdote : a
          )
        );
        setSelectedAnecdote(updatedAnecdote);
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {currentView !== "list" && (
              <button
                onClick={() => setCurrentView("list")}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {currentView === "list" &&
                t("anecdotes.modal.title", { chapterId: chapterNumber })}
              {currentView === "form" &&
                t("anecdotes.modal.formTitle", { chapterId: chapterNumber })}
              {currentView === "detail" && t("anecdotes.modal.detailTitle")}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {/* LIST VIEW */}
          {currentView === "list" && (
            <div className="space-y-6">
              <div className="flex justify-between">
                {/* Counters */}
                <div className="flex justify-center gap-6 text-center bg-gray-100 p-2 rounded-md">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      {t("anecdotes.modal.counters.sentRequests")}
                    </span>
                    <span className="text-l text-left font-bold text-gray-900 mt-1">
                      {sentCount}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      {t("anecdotes.modal.counters.receivedAnecdotes")}
                    </span>
                    <span className="text-l text-left font-bold text-green-600 mt-1">
                      {receivedCount}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      {t("anecdotes.modal.counters.expiredRequests")}
                    </span>
                    <span className="text-l text-left font-bold text-rust-500 mt-1">
                      {expiredCount}
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex justify-center items-center">
                  <button
                    onClick={() => setCurrentView("form")}
                    className="px-6 bg-delicate-500 text-white rounded-md hover:bg-delicate-600 transition-colors font-medium shadow-sm h-8"
                  >
                    {t("anecdotes.modal.requestFriend")}
                  </button>
                </div>
              </div>

              {/* Anecdotes List */}
              {chapterAnecdotes.length === 0 ? (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <svg
                      className="w-16 h-16 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm">
                    {t("anecdotes.modal.empty")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {chapterAnecdotes.map((anecdote) => (
                    <AnecdoteCard
                      key={anecdote.id}
                      anecdote={anecdote}
                      onClick={
                        anecdote.status !== "pending" &&
                        anecdote.status !== "expired"
                          ? () => handleViewDetail(anecdote)
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FORM VIEW */}
          {currentView === "form" && (
            <form onSubmit={handleSubmitForm} className="space-y-6">
              {/* Info Banner */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-yellow-800">
                  {t("anecdotes.modal.form.validityBanner")}
                </p>
              </div>
              <div className="bg-delicate-50 border border-delicate-200 rounded-lg p-4 flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-delicate-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-delicate-800">
                  {t("anecdotes.modal.form.infoBanner")}
                </p>
              </div>

              {/* Destinataires label */}
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  {t("anecdotes.modal.form.recipients")}
                </h3>
                <button
                  type="button"
                  onClick={handleAddFriend}
                  className="text-sm text-delicate-600 hover:text-delicate-700 border border-delicate-500 rounded-md p-2 font-medium flex items-center gap-1"
                >
                  <Plus size={16} />
                  {t("anecdotes.modal.form.addFriend")}
                </button>
              </div>

              {/* Friends list */}
              {friends.map((friend, index) => (
                <AnecdoteFriendForm
                  key={index}
                  index={index}
                  value={friend}
                  defaultMessage={getDefaultAnecdoteMessage(
                    friend.friend_first_name || "",
                    chapterTitle,
                    t
                  )}
                  onChange={handleFriendChange}
                  onRemove={handleRemoveFriend}
                  canRemove={friends.length > 1}
                />
              ))}

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setCurrentView("list")}
                  className="px-6 py-2.5 border border-rust-200 text-rust-600 rounded-md hover:bg-rust-10 transition-colors font-medium"
                >
                  {t("anecdotes.modal.form.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={createAnecdotesMutation.isLoading}
                  className="px-6 py-2.5 bg-herb-600 text-white rounded-md hover:bg-herb-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  {createAnecdotesMutation.isLoading
                    ? t("anecdotes.modal.form.sending")
                    : t("anecdotes.modal.form.send")}
                </button>
              </div>
            </form>
          )}

          {/* DETAIL VIEW */}
          {currentView === "detail" && selectedAnecdote && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex justify-between items-star pb-4">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedAnecdote.friend_first_name}{" "}
                    {selectedAnecdote.friend_last_name}
                  </p>
                </div>
                <div className="flex">
                  {/* N'affiche l'expiration que si l'anecdote n'a pas de contenu */}
                  {!selectedAnecdote.content && (
                    <p className="text-sm text-gray-500 mt-1 mr-2">
                      {selectedAnecdote.expired
                        ? t("anecdotes.modal.detail.expired")
                        : t("anecdotes.modal.detail.expiresIn", {
                            time: getTimeUntilExpiration(
                              selectedAnecdote.expires_at
                            ),
                          })}
                    </p>
                  )}
                  <AnecdoteStatusBadge status={selectedAnecdote.status} />
                </div>
              </div>

              {/* Photo if exists */}
              {selectedAnecdote.photo_url && (
                <div className="rounded-lg overflow-hidden max-w-md mx-auto">
                  <img
                    src={selectedAnecdote.photo_url}
                    alt={t("anecdotes.modal.detail.photoAlt")}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Content */}
              {selectedAnecdote.content && (
                <div className="bg-gray-100 rounded-lg p-6">
                  <p className="text-gray-800 whitespace-pre-line leading-relaxed">
                    {selectedAnecdote.content}
                  </p>
                </div>
              )}

              {/* Action buttons for submitted anecdotes */}
              {selectedAnecdote.status === "submitted" &&
                book?.status !== "pdf_generated" && (
                  <div className="flex justify-between items-center pt-4">
                    <button
                      onClick={() => setCurrentView("list")}
                      className="text-gray-600 hover:text-gray-800 font-medium underline"
                    >
                      {t("anecdotes.modal.detail.back")}
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={handleReject}
                        disabled={rejectMutation.isLoading}
                        className="px-6 py-2.5 border border-rust-500 text-rust-600 rounded-md hover:bg-rust-10 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed font-medium"
                      >
                        {rejectMutation.isLoading
                          ? t("anecdotes.modal.detail.rejecting")
                          : t("anecdotes.modal.detail.reject")}
                      </button>
                      <button
                        onClick={handleAccept}
                        disabled={acceptMutation.isLoading}
                        className="px-6 py-2.5 bg-herb-600 text-white rounded-md hover:bg-herb-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm"
                      >
                        {acceptMutation.isLoading
                          ? t("anecdotes.modal.detail.accepting")
                          : t("anecdotes.modal.detail.accept")}
                      </button>
                    </div>
                  </div>
                )}

              {/* Can switch decision if accepted/rejected and PDF not generated */}
              {(selectedAnecdote.status === "accepted" ||
                selectedAnecdote.status === "rejected") &&
                book?.status !== "pdf_generated" && (
                  <div className="flex justify-between items-center pt-4">
                    <button
                      onClick={() => setCurrentView("list")}
                      className="text-gray-600 hover:text-gray-800 font-medium underline"
                    >
                      {t("anecdotes.modal.detail.back")}
                    </button>
                    {selectedAnecdote.status === "accepted" ? (
                      <button
                        onClick={handleReject}
                        disabled={rejectMutation.isLoading}
                        className="px-6 py-2.5 border border-rust-500 text-rust-600 rounded-md hover:bg-rust-10 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed font-medium"
                      >
                        {rejectMutation.isLoading
                          ? t("anecdotes.modal.detail.changing")
                          : t("anecdotes.modal.detail.reject")}
                      </button>
                    ) : (
                      <button
                        onClick={handleAccept}
                        disabled={acceptMutation.isLoading}
                        className="px-6 py-2.5 bg-herb-600 text-white rounded-md hover:bg-herb-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm"
                      >
                        {acceptMutation.isLoading
                          ? t("anecdotes.modal.detail.changing")
                          : t("anecdotes.modal.detail.changeToAccepted")}
                      </button>
                    )}
                  </div>
                )}

              {/* PDF generated warning */}
              {book?.status === "pdf_generated" && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    {t("anecdotes.modal.detail.pdfGeneratedWarning")}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
